<template>
  <div class="max-w-7xl mx-auto p-6">
    <h1 class="text-3xl font-bold text-gray-800 mb-6">Local Delivery</h1>

    <!-- Tabs -->
    <div class="border-b border-gray-200 mb-6">
      <nav class="flex space-x-6">
        <button @click="activeTab = 'zipcodes'" :class="tabClass('zipcodes')">
          ZIP codes
        </button>
        <button @click="activeTab = 'rates'" :class="tabClass('rates')">
          Rates
        </button>
      </nav>
    </div>

    <!-- ZIP CODES TAB -->
    <section v-if="activeTab === 'zipcodes'">
      <div class="bg-white shadow-sm border border-gray-200 rounded-xl p-6">
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Deposit
          </label>
          <select
            v-model="selectedDepositoId"
            @change="loadZipcodes"
            class="w-full md:w-1/2 px-3 py-2 border rounded-md"
          >
            <option :value="null">-- Select a deposit --</option>
            <option
              v-for="dep in depositos"
              :key="dep.depositoId"
              :value="dep.depositoId"
            >
              {{ dep.name }} ({{ dep.state }})
            </option>
          </select>
        </div>

        <div v-if="selectedDepositoId" class="mt-6">
          <div class="flex items-end gap-2 mb-4">
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Add ZIP codes (comma or newline separated)
              </label>
              <textarea
                v-model="bulkZipInput"
                rows="3"
                placeholder="33101, 33102, 33103"
                class="w-full px-3 py-2 border rounded-md"
              ></textarea>
            </div>
            <button
              @click="addZipcodes"
              :disabled="loading || !bulkZipInput.trim()"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Add
            </button>
          </div>

          <table class="min-w-full text-sm border">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-4 py-2 text-left">ZIP</th>
                <th class="px-4 py-2 text-left">Status</th>
                <th class="px-4 py-2 text-left">Created</th>
                <th class="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="z in zipcodes"
                :key="z.id"
                class="border-b hover:bg-gray-50"
              >
                <td class="px-4 py-2">{{ z.zipcode }}</td>
                <td class="px-4 py-2">
                  <span
                    :class="z.status === 1 ? 'text-green-700' : 'text-gray-500'"
                  >
                    {{ z.status == 1 ? "Active" : "Inactive" }}
                  </span>
                </td>
                <td class="px-4 py-2">{{ z.createdAt }}</td>
                <td class="px-4 py-2 text-right">
                  <button
                    @click="removeZip(z.id)"
                    class="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </td>
              </tr>
              <tr v-if="!zipcodes.length">
                <td colspan="4" class="px-4 py-6 text-center text-gray-500">
                  No ZIP codes for this deposit yet.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- RATES TAB -->
    <section v-if="activeTab === 'rates'">
      <div
        v-if="rates"
        class="bg-white shadow-sm border border-gray-200 rounded-xl p-6 space-y-4"
      >
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label class="flex items-center gap-2">
            <input type="checkbox" v-model="rates.enabled" />
            <span class="text-sm">Enabled</span>
          </label>
          <div>
            <label class="block text-sm font-medium mb-1">Currency</label>
            <input
              type="text"
              v-model="rates.currency"
              class="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">
              Max distance (mi)
            </label>
            <input
              type="number"
              v-model.number="rates.maxDistanceMiles"
              class="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Min order</label>
            <input
              type="number"
              v-model.number="rates.minOrder"
              class="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">
              Open rate per mile
            </label>
            <input
              type="number"
              step="0.01"
              v-model.number="rates.openRatePerMile"
              class="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">
              Open rate min
            </label>
            <input
              type="number"
              v-model.number="rates.openRateMin"
              class="w-full px-3 py-2 border rounded-md"
            />
          </div>
        </div>

        <div>
          <h3 class="font-semibold mb-2">Blocks (by distance)</h3>
          <table class="min-w-full text-sm border">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-3 py-2 text-left">From (mi)</th>
                <th class="px-3 py-2 text-left">To (mi)</th>
                <th class="px-3 py-2 text-left">Price ($)</th>
                <th class="px-3 py-2 text-left">Free above ($)</th>
                <th class="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(b, idx) in rates.blocks" :key="idx" class="border-b">
                <td class="px-3 py-2">
                  <input
                    type="number"
                    v-model.number="b.from"
                    class="w-24 px-2 py-1 border rounded"
                  />
                </td>
                <td class="px-3 py-2">
                  <input
                    type="number"
                    v-model.number="b.to"
                    class="w-24 px-2 py-1 border rounded"
                  />
                </td>
                <td class="px-3 py-2">
                  <input
                    type="number"
                    step="0.01"
                    v-model.number="b.price"
                    class="w-24 px-2 py-1 border rounded"
                  />
                </td>
                <td class="px-3 py-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Optional"
                    v-model.number="b.freeAbove"
                    class="w-24 px-2 py-1 border rounded"
                  />
                </td>
                <td class="px-3 py-2 text-right">
                  <button
                    @click="removeBlock(idx)"
                    class="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <button
            @click="addBlock"
            class="mt-2 px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
          >
            + Add block
          </button>
          <p v-if="blocksError" class="text-red-600 text-sm mt-2">
            {{ blocksError }}
          </p>
        </div>

        <div class="text-right">
          <button
            @click="saveRates"
            :disabled="loading || !!blocksError"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            Save rates
          </button>
        </div>
      </div>
      <div v-else class="text-gray-500">Loading rates…</div>
    </section>
  </div>
</template>

<script>
import httpStore from "@/api/httpStore.js";
import {
  getLocalDeliveryZipcodes,
  addLocalDeliveryZipcodes,
  deleteLocalDeliveryZipcode,
  getLocalDeliveryRates,
  updateLocalDeliveryRates
} from "@/api/store/SettingsService.js";

export default {
  name: "LocalDelivery",
  data() {
    return {
      activeTab: "zipcodes",
      depositos: [],
      selectedDepositoId: null,
      zipcodes: [],
      bulkZipInput: "",
      rates: null,
      loading: false
    };
  },
  computed: {
    blocksError() {
      if (!this.rates || !Array.isArray(this.rates.blocks)) return null;
      const sorted = [...this.rates.blocks].sort((a, b) => a.from - b.from);
      for (let i = 0; i < sorted.length; i++) {
        const b = sorted[i];
        if (b.to <= b.from) return `Block ${i + 1}: "to" must be > "from"`;
        if (b.price < 0) return `Block ${i + 1}: price must be >= 0`;
        if (i > 0 && b.from < sorted[i - 1].to) {
          return `Blocks overlap between ${sorted[i - 1].from}-${
            sorted[i - 1].to
          } and ${b.from}-${b.to}`;
        }
      }
      return null;
    }
  },
  async mounted() {
    await Promise.all([this.loadDepositos(), this.loadRates()]);
  },
  methods: {
    tabClass(tab) {
      return [
        "py-2 px-1 text-sm font-medium border-b-2",
        this.activeTab === tab
          ? "border-blue-600 text-blue-600"
          : "border-transparent text-gray-500 hover:text-gray-700"
      ];
    },
    async loadDepositos() {
      try {
        const res = await httpStore.get("local_delivery/depositos");
        this.depositos = res.data?.data || [];
      } catch (e) {
        this.$toast.open({
          message: "Error loading deposits",
          type: "error",
          position: "top-right"
        });
      }
    },
    async loadZipcodes() {
      if (!this.selectedDepositoId) {
        this.zipcodes = [];
        return;
      }
      try {
        const res = await getLocalDeliveryZipcodes(this.selectedDepositoId);
        this.zipcodes = res?.data || [];
      } catch (e) {
        this.zipcodes = [];
      }
    },
    async addZipcodes() {
      const parsed = this.bulkZipInput
        .split(/[\s,]+/)
        .map(z => z.trim())
        .filter(Boolean);
      if (!parsed.length) return;
      this.loading = true;
      try {
        await addLocalDeliveryZipcodes(this.selectedDepositoId, parsed);
        this.bulkZipInput = "";
        await this.loadZipcodes();
      } catch (e) {
        this.$toast.open({
          message: "Error adding ZIP codes",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loading = false;
      }
    },
    async removeZip(id) {
      if (!confirm("Delete this ZIP code?")) return;
      try {
        await deleteLocalDeliveryZipcode(id);
        await this.loadZipcodes();
      } catch (e) {
        this.$toast.open({
          message: "Error deleting ZIP code",
          type: "error",
          position: "top-right"
        });
      }
    },
    async loadRates() {
      try {
        const res = await getLocalDeliveryRates();
        this.rates = res?.data || this.defaultRates();
        if (!Array.isArray(this.rates.blocks)) this.rates.blocks = [];
      } catch (e) {
        this.rates = this.defaultRates();
      }
    },
    defaultRates() {
      return {
        enabled: false,
        currency: "USD",
        minOrder: 0,
        blocks: [],
        openRatePerMile: 0,
        openRateMin: 0,
        maxDistanceMiles: 0
      };
    },
    addBlock() {
      this.rates.blocks.push({ from: 0, to: 0, price: 0, freeAbove: null });
    },
    removeBlock(idx) {
      this.rates.blocks.splice(idx, 1);
    },
    async saveRates() {
      if (this.blocksError) return;
      this.loading = true;
      // API uses snake_case
      const payload = {
        enabled: !!this.rates.enabled,
        currency: this.rates.currency,
        min_order: this.rates.minOrder || 0,
        blocks: this.rates.blocks.map(b => ({
          from: b.from,
          to: b.to,
          price: b.price,
          free_above:
            b.freeAbove === "" || b.freeAbove == null ? null : b.freeAbove
        })),
        open_rate_per_mile: this.rates.openRatePerMile || 0,
        open_rate_min: this.rates.openRateMin || 0,
        max_distance_miles: this.rates.maxDistanceMiles || 0
      };
      try {
        await updateLocalDeliveryRates(payload);
        this.$toast.open({
          message: "Rates saved",
          type: "success",
          position: "top-right"
        });
      } catch (e) {
        this.$toast.open({
          message: "Error saving rates",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
